// System-wide calling provider (RingCentral | Twilio).
//
// The value is stored in the DB (SystemSettings.CallProvider) and is shared by
// ALL users — it is no longer a per-user localStorage preference. localStorage
// is kept only as a fast local cache so the many synchronous
// `localStorage.getItem("selectedCallProvider")` reads across the app keep
// working; it is (re)seeded from the DB on app load and whenever the value is
// changed (locally or by another client via the SignalR "CallProviderChanged"
// broadcast).
import { getCallProvider, setCallProvider } from "./api/apiManager";

export const DEFAULT_CALL_PROVIDER = "RingCentral";
export const CALL_PROVIDER_STORAGE_KEY = "selectedCallProvider";
// Window event other components listen to so their local state updates live.
export const CALL_PROVIDER_EVENT = "callProviderChanged";

// Current cached provider (never null).
export const getLocalCallProvider = () =>
  localStorage.getItem(CALL_PROVIDER_STORAGE_KEY) || DEFAULT_CALL_PROVIDER;

// Write the provider to the local cache and notify the app (no server call).
// Used by the app-load sync and the SignalR broadcast handler.
export const applyCallProvider = (provider) => {
  if (!provider) return;
  localStorage.setItem(CALL_PROVIDER_STORAGE_KEY, provider);
  window.dispatchEvent(
    new CustomEvent(CALL_PROVIDER_EVENT, { detail: provider }),
  );
};

// Fetch the system provider from the DB and apply it locally. Call once on app
// load. Returns the provider string (falls back to the cached/default value if
// the request fails).
export const syncCallProviderFromServer = async () => {
  const res = await getCallProvider();
  const provider = res?.data?.provider;
  if (provider) {
    applyCallProvider(provider);
    return provider;
  }
  return getLocalCallProvider();
};

// Change the system provider: persist to DB (which broadcasts to every client)
// and apply locally right away. Returns the saved provider or null on failure.
export const changeCallProvider = async (provider, userId) => {
  const res = await setCallProvider(provider, userId);
  const saved = res?.data?.provider;
  if (saved) {
    applyCallProvider(saved);
    return saved;
  }
  return null;
};
